<template>
    <div class="flex flex-col dark:text-white">
        <div class="mt-4 flex flex-wrap items-center gap-4">
            <h1 class="text-2xl font-bold">Nexus</h1>
            <div class="flex items-center gap-3">
                <label class="text-sm font-semibold">Parceiro</label>
                <select
                    v-model="selectedPartnerId"
                    @change="handlePartnerChange"
                    class="rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                    <option value="all">Todos</option>
                    <option v-for="partner in partners" :key="partner.id" :value="partner.id">
                        {{ partner.name }}
                    </option>
                </select>
            </div>
            <button
                class="ml-auto rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                @click="openCreateModal"
            >
                Novo Parceiro
            </button>
        </div>

        <div class="grid gap-6 my-4 md:grid-cols-2">
            <card :loading="metricsLoading">
                <p class="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                    Onboarding Aprovados
                </p>
                <p class="text-lg font-semibold text-white-100 dark:text-gray-200">
                    {{ metrics.approved_onboards }}
                </p>
            </card>
            <card :loading="metricsLoading">
                <p class="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                    Faturamento Total
                </p>
                <p class="text-lg font-semibold text-white-100 dark:text-gray-200">
                    {{ $root.formatCurrency(metrics.total_billing) }}
                </p>
            </card>
        </div>

        <div class="border-b border-gray-200 dark:border-gray-700">
            <nav class="-mb-px flex gap-4">
                <button
                    class="border-b-2 px-4 py-2 text-sm font-semibold"
                    :class="tabClass('onboard')"
                    @click="changeTab('onboard')"
                >
                    Onboarding
                </button>
                <button
                    class="border-b-2 px-4 py-2 text-sm font-semibold"
                    :class="tabClass('sales')"
                    @click="changeTab('sales')"
                >
                    Vendas
                </button>
                <button
                    class="border-b-2 px-4 py-2 text-sm font-semibold"
                    :class="tabClass('settings')"
                    @click="changeTab('settings')"
                >
                    Configurações
                </button>
                <button
                    class="border-b-2 px-4 py-2 text-sm font-semibold"
                    :class="tabClass('bank-accounts')"
                    @click="changeTab('bank-accounts')"
                >
                    Contas Bancárias
                </button>
            </nav>
        </div>

        <section v-if="activeTab === 'onboard'" class="mt-4">
            <filters-onboard
                v-model="onboardFilters"
                :loading="onboardLoading"
                :default-dates="defaultOnboardDates"
                :statuses="onboardStatusOptions"
                :person-types="onboardTypeOptions"
                class="mt-4"
                @fetch="handleOnboardFetch"
            />

            <table-component
                class="mt-6"
                :loading="onboardLoading"
                :pagination="onboardPagination"
                :headers="[
                    'ID',
                    'Nome',
                    'E-mail',
                    'Telefone',
                    'Pessoa',
                    'Data de Criação',
                    'Status do Onboarding',
                ]"
                @fetch="fetchOnboards"
            >
                <tr v-for="user in onboardUsers" :key="user.id">
                    <td class="table-cell-sm">
                        <router-link
                            :to="{ name: 'compliance.details', params: { user_id: user.id } }"
                            class="text-indigo-400 hover:underline hover:text-indigo-700 hover:cursor-pointer"
                        >
                            #{{ user.id }}
                        </router-link>
                    </td>
                    <td class="table-cell-sm">{{ user.name }}</td>
                    <td class="table-cell-sm">{{ user.email }}</td>
                    <td class="table-cell-sm">{{ user.formatted_phone }}</td>
                    <td class="table-cell-sm">{{ user.formatted_type }}</td>
                    <td class="table-cell-sm text-xs">{{ formatDateWithoutTime(user.created_at) }}</td>
                    <td class="table-cell-sm">
                        <span class="flex items-center">
                            <span
                                class="mr-2 h-2.5 w-2.5 rounded-full"
                                :class="statusDotClass(user.status)"
                            ></span>
                            {{ getStatusName(user.status) }}
                        </span>
                    </td>
                </tr>
            </table-component>
        </section>

        <section v-else-if="activeTab === 'sales'" class="mt-4">
            <filters-orders
                v-model="ordersFilters"
                :loading="ordersLoading"
                date-filter
                :default-dates="defaultOrderDates"
                status-filter
                payment-method-filter
                producer-email-filter
                class="mt-4"
                @fetch="handleOrdersFetch"
            />

            <table-component
                :loading="ordersLoading"
                :pagination="ordersPagination"
                :headers="[
                    'Pedido',
                    'Produto',
                    'Produtor',
                    'Cliente',
                    'Método de Pagamento',
                    'Valor do Pedido',
                    'Data do Pedido',
                    'Status',
                ]"
                @fetch="fetchOrders"
            >
                <tr v-for="order in orders" :key="order.id">
                    <td class="table-cell-sm">
                        <router-link
                            :to="{ name: 'user.order', params: { order_id: order.id } }"
                            class="text-indigo-400 hover:underline hover:text-indigo-700 hover:cursor-pointer"
                        >
                            #{{ order.id }}
                        </router-link>
                    </td>
                    <td class="table-cell-sm">
                        {{ order.product_name }}
                    </td>
                    <td class="table-cell-sm">
                        <button
                            class="text-indigo-500 hover:underline"
                            @click.prevent="manageUser(order.producer.id)"
                        >
                            {{ order.producer.name }}
                        </button>
                    </td>
                    <td class="table-cell-sm text-xs">
                        {{ order.customer ? order.customer.name : order.customer_name }}
                    </td>
                    <td class="table-cell-sm">
                        <payment-type
                            :method="order.transactions[0] ? order.transactions[0].payment_method : null"
                            :hasAmountCard2="order.amount_card2"
                            :brandCard1="order.transactions[0] && order.transactions[0].card ? order.transactions[0].card : null"
                            :brandCard2="order.transactions[1] && order.transactions[1].card ? order.transactions[1].card : null"
                        />
                    </td>
                    <td class="table-cell-sm">
                        {{ $root.formatCurrency(order.amount + order.amount_card2) }}
                    </td>
                    <td class="table-cell-sm text-xs">{{ order.created_at }}</td>
                    <td class="table-cell-sm">
                        <status
                            size="sm"
                            :status="order.transaction_status"
                            :isRefundFromHood="order.transactions[order.transactions.length - 1].is_refund_from_hood"
                        />
                    </td>
                </tr>
            </table-component>
        </section>

        <section v-else-if="activeTab === 'bank-accounts'" class="mt-4">
            <div class="flex flex-wrap items-center gap-3 mb-4">
                <input
                    v-model="baSearch"
                    type="text"
                    placeholder="Buscar por nome, código, email, pix..."
                    class="w-72 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    @keyup.enter="handleBaSearch"
                />
                <select
                    v-model="baStatusFilter"
                    class="rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    @change="handleBaSearch"
                >
                    <option value="">Todos os status</option>
                    <option value="approved">Aprovado</option>
                    <option value="pending">Pendente</option>
                    <option value="reproved">Reprovado</option>
                </select>
                <select
                    v-model="baDeletedFilter"
                    class="rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    @change="handleBaSearch"
                >
                    <option value="">Ativas</option>
                    <option value="only">Apenas Excluídas</option>
                </select>
                <button
                    class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                    @click="handleBaSearch"
                >
                    Buscar
                </button>
            </div>

            <table-component
                :loading="baLoading"
                :pagination="baPagination"
                :headers="['ID', 'Código', 'Titular', 'Usuário', 'Banco', 'Tipo', 'Status', 'Ações']"
                @fetch="fetchBankAccounts"
            >
                <tr v-for="account in bankAccounts" :key="account.id" :class="{ 'opacity-50': account.deleted_at }">
                    <td class="table-cell-sm">#{{ account.id }}</td>
                    <td class="table-cell-sm">
                        <span v-if="account.code" class="font-mono text-xs">{{ account.code }}</span>
                        <button
                            v-else
                            class="rounded bg-amber-600 px-2 py-1 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                            @click="generateBaCode(account)"
                            :disabled="baActionLoading"
                        >
                            Gerar Código
                        </button>
                    </td>
                    <td class="table-cell-sm">{{ account.holder_name }}</td>
                    <td class="table-cell-sm">
                        <router-link
                            v-if="account.user"
                            :to="{ name: 'compliance.details', params: { user_id: account.user.id } }"
                            class="text-indigo-400 hover:underline"
                        >
                            {{ account.user.name }}
                        </router-link>
                    </td>
                    <td class="table-cell-sm">{{ account.bank?.name || '-' }}</td>
                    <td class="table-cell-sm">{{ account.type === 'checking' ? 'Corrente' : (account.type === 'savings' ? 'Poupança' : account.type || '-') }}</td>
                    <td class="table-cell-sm">
                        <span class="flex items-center">
                            <span class="mr-2 h-2.5 w-2.5 rounded-full" :class="baStatusDot(account.status)"></span>
                            {{ baStatusLabel(account.status) }}
                        </span>
                    </td>
                    <td class="table-cell-sm">
                        <div class="flex gap-1">
                            <button
                                v-if="!account.deleted_at"
                                class="rounded bg-indigo-600 px-2 py-1 text-xs text-white hover:bg-indigo-700"
                                @click="openBaEditModal(account)"
                            >
                                Editar
                            </button>
                            <button
                                v-if="!account.deleted_at"
                                class="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700 disabled:opacity-50"
                                @click="deleteBa(account)"
                                :disabled="baActionLoading"
                            >
                                Excluir
                            </button>
                            <button
                                v-if="account.deleted_at"
                                class="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700 disabled:opacity-50"
                                @click="restoreBa(account)"
                                :disabled="baActionLoading"
                            >
                                Restaurar
                            </button>
                        </div>
                    </td>
                </tr>
            </table-component>
        </section>

        <!-- Modal Edit Bank Account -->
        <div
            v-if="showBaModal"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            @click.self="showBaModal = false"
        >
            <div class="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
                <h2 class="mb-4 text-lg font-bold">Editar Conta Bancária #{{ editingBa?.id }}</h2>
                <div class="space-y-4">
                    <div>
                        <label class="mb-1 block text-sm font-semibold">Titular</label>
                        <input v-model="baForm.holder_name" type="text" class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="mb-1 block text-sm font-semibold">Banco</label>
                            <select v-model="baForm.bank_id" class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                                <option v-for="bank in banks" :key="bank.id" :value="bank.id">{{ bank.name }}</option>
                            </select>
                        </div>
                        <div>
                            <label class="mb-1 block text-sm font-semibold">Tipo de Recebimento</label>
                            <select v-model="baForm.receiver_type" class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                                <option value="pix">PIX</option>
                                <option value="bank">TED</option>
                            </select>
                        </div>
                    </div>
                    <div v-if="baForm.receiver_type === 'pix'">
                        <label class="mb-1 block text-sm font-semibold">Chave PIX</label>
                        <input v-model="baForm.pix_key" type="text" class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                    </div>
                    <div v-if="baForm.receiver_type === 'bank'" class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="mb-1 block text-sm font-semibold">Agência</label>
                            <input v-model="baForm.branch_number" type="text" class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label class="mb-1 block text-sm font-semibold">Conta</label>
                            <input v-model="baForm.account_number" type="text" class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
                        </div>
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="mb-1 block text-sm font-semibold">Tipo de Conta</label>
                            <select v-model="baForm.type" class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                                <option value="checking">Corrente</option>
                                <option value="savings">Poupança</option>
                            </select>
                        </div>
                        <div>
                            <label class="mb-1 block text-sm font-semibold">Status</label>
                            <p class="rounded-lg border border-gray-200 bg-gray-100 p-2.5 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                {{ baStatusLabel(editingBa?.status) }}
                                <span class="ml-1 text-xs text-gray-500">(gerenciado pelo fluxo de validação)</span>
                            </p>
                        </div>
                    </div>
                    <div v-if="editingBa?.code" class="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                        <span class="text-sm font-semibold">Código:</span>
                        <span class="ml-2 font-mono text-sm">{{ editingBa.code }}</span>
                        <span class="ml-2 text-xs text-gray-500">(não editável)</span>
                    </div>
                </div>
                <div class="mt-6 flex justify-end gap-3">
                    <button class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700" @click="showBaModal = false">Cancelar</button>
                    <button class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50" :disabled="baSaving" @click="saveBa">
                        {{ baSaving ? 'Salvando...' : 'Salvar' }}
                    </button>
                </div>
            </div>
        </div>

        <section v-else class="mt-4">
            <div v-if="selectedPartnerId === 'all'" class="rounded-lg bg-slate-50 p-4 text-sm dark:bg-slate-800/25">
                Selecione um parceiro para visualizar as configurações.
            </div>
            <div v-else>
                <ticto-loading v-if="settingsLoading" />
                <div v-else class="space-y-4">
                    <div class="flex items-center gap-2">
                        <button
                            class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                            @click="openEditModal"
                        >
                            Editar Parceiro
                        </button>
                        <button
                            v-if="clientShowData"
                            class="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
                            @click="deleteClient"
                        >
                            Excluir Parceiro
                        </button>
                    </div>

                    <div class="rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25">
                        <p class="text-sm font-semibold">URL de arquivos</p>
                        <p class="text-sm text-gray-600 dark:text-gray-300">
                            {{ settings?.settings?.files_domain || '-' }}
                        </p>
                        <p class="mt-3 text-sm font-semibold">URL de Webhook</p>
                        <p class="text-sm text-gray-600 dark:text-gray-300">
                            {{ settings?.settings?.webhook_endpoint || '-' }}
                        </p>

                        <div class="mt-3">
                            <p class="text-sm font-semibold">Token de API</p>
                            <div class="flex items-center gap-2 mt-1">
                                <code class="rounded bg-gray-200 px-2 py-1 text-xs dark:bg-gray-700 font-mono select-all">
                                    {{ tokenVisible ? revealedToken : '••••••••••••••••••••' }}
                                </code>
                                <button
                                    class="rounded bg-gray-200 px-2 py-1 text-xs hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
                                    @click="toggleToken"
                                    :disabled="tokenRevealing"
                                >
                                    {{ tokenRevealing ? '...' : (tokenVisible ? 'Ocultar' : 'Revelar') }}
                                </button>
                                <button
                                    v-if="tokenVisible && revealedToken"
                                    class="rounded bg-gray-200 px-2 py-1 text-xs hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
                                    @click="copyToClipboard(revealedToken)"
                                >
                                    Copiar
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25 space-y-3">
                        <form-toggle
                            v-model="settingsForm.client_active"
                            :loading="settingsSaving"
                            label="Ativar integração total via Nexus"
                            @update:modelValue="updateSettings"
                        />

                        <div class="mt-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                            <form-toggle
                                v-model="limitEnabled"
                                :loading="settingsSaving"
                                label="Limitar quantidade de solicitações de onboarding via Nexus"
                                @update:modelValue="handleLimitToggle"
                            />
                            <div v-if="limitEnabled" class="mt-3 flex flex-wrap items-center gap-3">
                                <input
                                    v-model.number="limitValue"
                                    type="number"
                                    min="1"
                                    class="w-40 rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                />
                                <button
                                    class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                                    :disabled="settingsSaving"
                                    @click.prevent="saveLimit"
                                >
                                    Salvar limite
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25">
                        <h3 class="text-sm font-semibold mb-3">Eventos de webhooks</h3>
                        <div class="grid gap-2 md:grid-cols-2">
                            <div
                                v-for="event in settings?.available_events || []"
                                :key="event"
                                class="flex items-center justify-between rounded border border-gray-200 p-2 text-sm dark:border-gray-700"
                            >
                                <span>{{ formatEventLabel(event) }}</span>
                                <span
                                    class="rounded px-2 py-1 text-xs font-semibold"
                                    :class="isEventActive(event) ? 'bg-green-600 text-white' : 'bg-red-600 text-white'"
                                >
                                    {{ isEventActive(event) ? 'Habilitado' : 'Desabilitado' }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Modal Create/Edit -->
        <div
            v-if="showClientModal"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            @click.self="showClientModal = false"
        >
            <div class="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
                <h2 class="mb-4 text-lg font-bold">
                    {{ editingClientId ? 'Editar Parceiro' : 'Novo Parceiro' }}
                </h2>

                <div class="space-y-4">
                    <div>
                        <label class="mb-1 block text-sm font-semibold">Nome *</label>
                        <input
                            v-model="clientForm.name"
                            type="text"
                            class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            placeholder="Nome do parceiro"
                        />
                    </div>

                    <div class="flex items-center gap-4">
                        <form-toggle v-model="clientForm.active" label="Ativo" />
                    </div>

                    <hr class="border-gray-200 dark:border-gray-700" />

                    <h3 class="text-sm font-bold text-gray-500 dark:text-gray-400">Configurações</h3>

                    <div>
                        <label class="mb-1 block text-sm font-semibold">Webhook Endpoint</label>
                        <input
                            v-model="clientForm.webhook_endpoint"
                            type="url"
                            class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            placeholder="https://..."
                        />
                    </div>

                    <div>
                        <label class="mb-1 block text-sm font-semibold">Files Domain</label>
                        <input
                            v-model="clientForm.files_domain"
                            type="url"
                            class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            placeholder="https://..."
                        />
                    </div>

                    <div class="flex flex-wrap gap-4">
                        <form-toggle v-model="clientForm.sales_active" label="Vendas Ativas" />
                        <form-toggle v-model="clientForm.onboarding_active" label="Onboarding Ativo" />
                    </div>

                    <div>
                        <label class="mb-1 block text-sm font-semibold">Limite de Usuários</label>
                        <input
                            v-model.number="clientForm.user_limit"
                            type="number"
                            min="1"
                            class="w-40 rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            placeholder="Sem limite"
                        />
                    </div>

                    <div>
                        <label class="mb-2 block text-sm font-bold">Eventos de Webhook</label>
                        <div class="grid gap-2 md:grid-cols-2">
                            <label
                                v-for="event in availableEvents"
                                :key="event"
                                class="flex items-center gap-2 rounded border border-gray-200 p-2 text-sm dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                            >
                                <input
                                    type="checkbox"
                                    :value="event"
                                    v-model="clientForm.events"
                                    class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600"
                                />
                                {{ formatEventLabel(event) }}
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Token exibido apos criacao -->
                <div v-if="createdToken" class="mt-4 rounded-lg border border-green-300 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/20">
                    <p class="text-sm font-bold text-green-700 dark:text-green-400">Token gerado (copie agora, ele não será exibido novamente):</p>
                    <div class="mt-2 flex items-center gap-2">
                        <code class="flex-1 rounded bg-white px-3 py-2 text-xs font-mono dark:bg-gray-800 select-all break-all">
                            {{ createdToken }}
                        </code>
                        <button
                            class="rounded bg-green-600 px-3 py-2 text-xs font-semibold text-white hover:bg-green-700"
                            @click="copyToClipboard(createdToken)"
                        >
                            Copiar
                        </button>
                    </div>
                </div>

                <div class="mt-6 flex justify-end gap-3">
                    <button
                        class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
                        @click="showClientModal = false"
                    >
                        {{ createdToken ? 'Fechar' : 'Cancelar' }}
                    </button>
                    <button
                        v-if="!createdToken"
                        class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                        :disabled="clientSaving || !clientForm.name"
                        @click="saveClient"
                    >
                        {{ clientSaving ? 'Salvando...' : (editingClientId ? 'Salvar' : 'Criar') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useToast } from "vue-toastification";
import Card from "../../components/layouts/Card.vue";
import FiltersOnboard from "../../components/layouts/FiltersOnboard.vue";
import FiltersOrders from "../../components/layouts/FiltersOrders.vue";
import FormToggle from "../../components/forms/FormToggle.vue";
import PaymentType from "../../components/layouts/PaymentType.vue";
import Status from "../../components/layouts/Status.vue";
import TableComponent from "../../components/layouts/table/TableComponent.vue";
import TictoLoading from "../../components/layouts/TictoLoading.vue";
import HelperMixin from "../../mixins/HelperMixin.vue";

export default {
    components: {
        Card,
        FiltersOnboard,
        FiltersOrders,
        FormToggle,
        PaymentType,
        Status,
        TableComponent,
        TictoLoading,
    },
    mixins: [HelperMixin],
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            partners: [],
            selectedPartnerId: "all",
            metrics: {
                approved_onboards: 0,
                total_billing: 0,
            },
            metricsLoading: false,
            activeTab: "onboard",
            onboardFilters: {},
            onboardUsers: [],
            onboardLoading: false,
            onboardPagination: {
                total: 0,
                per_page: 25,
                current_page: 1,
                last_page: 1,
                from: 0,
                to: 0,
            },
            ordersFilters: {},
            orders: [],
            ordersLoading: false,
            ordersPagination: {
                total: 0,
                per_page: 10,
                current_page: 1,
                last_page: 1,
                from: 1,
                to: 1,
            },
            defaultOrderDates: [
                moment().subtract(1, "month").format("DD/MM/YYYY"),
                moment().format("DD/MM/YYYY"),
            ],
            defaultOnboardDates: [
                moment().subtract(1, "month").format("DD/MM/YYYY"),
                moment().format("DD/MM/YYYY"),
            ],
            settingsLoading: false,
            settingsSaving: false,
            settings: null,
            settingsForm: {
                client_active: true,
                user_limit: null,
            },
            limitEnabled: false,
            limitValue: null,

            // CRUD state
            showClientModal: false,
            editingClientId: null,
            clientForm: {
                name: "",
                active: true,
                webhook_endpoint: "",
                files_domain: "",
                events: [],
                sales_active: true,
                onboarding_active: true,
                user_limit: null,
            },
            clientSaving: false,
            createdToken: null,
            clientShowData: null,
            availableEvents: [],

            // Token reveal
            revealedToken: null,
            tokenVisible: false,
            tokenRevealing: false,

            // Bank Accounts tab
            bankAccounts: [],
            baLoading: false,
            baPagination: { total: 0, per_page: 25, current_page: 1, last_page: 1, from: 0, to: 0 },
            baSearch: "",
            baStatusFilter: "",
            baDeletedFilter: "",
            baActionLoading: false,
            showBaModal: false,
            editingBa: null,
            baForm: { holder_name: "", bank_id: null, type: "checking", branch_number: "", account_number: "", account_check_digit: "", pix_key: "", receiver_type: "pix" },
            baSaving: false,
            banks: [],
        };
    },
    computed: {
        onboardStatusOptions() {
            return [
                { value: "onboarding-waiting", label: "Aguardando Onboarding" },
                { value: "onboarding-pending", label: "Onboarding Pendente" },
                { value: "onboarding-approved", label: "Onboarding Aprovado" },
                { value: "onboarding-reproved", label: "Onboarding Reprovado" },
                { value: "onboarding-pending-correction", label: "Onboarding Pendente de Correção" },
                { value: "pending", label: "Pendentes de Análise" },
                { value: "pending-correction", label: "Pendentes de Correção" },
                { value: "approved", label: "Aprovado" },
                { value: "reproved", label: "Reprovado" },
            ];
        },
        onboardTypeOptions() {
            return [
                { value: "person", label: "Física" },
                { value: "company", label: "Jurídica" },
            ];
        },
    },
    watch: {
    },
    mounted() {
        this.fetchPartners().then(() => {
            this.fetchMetrics();
            this.fetchOnboards();
        });
    },
    methods: {
        tabClass(tab) {
            return this.activeTab === tab
                ? "border-indigo-500 text-indigo-500"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300";
        },
        changeTab(tab) {
            this.activeTab = tab;
            if (tab === "onboard") {
                this.fetchOnboards();
            }
            if (tab === "sales") {
                this.handleOrdersFetch();
            }
            if (tab === "settings" && this.selectedPartnerId !== "all") {
                this.fetchSettings();
            }
            if (tab === "bank-accounts") {
                this.fetchBankAccounts();
                if (!this.banks.length) this.fetchBanks();
            }
        },
        partnerParam() {
            return this.selectedPartnerId === "all" ? null : this.selectedPartnerId;
        },
        async fetchPartners() {
            try {
                const { data } = await axios.get("/api/nexus/partners");
                this.partners = data.data || [];
                if (data.available_events) {
                    this.availableEvents = data.available_events;
                }
            } catch (error) {
                this.toast.error("Erro ao carregar parceiros Nexus.");
            }
        },
        async fetchMetrics() {
            this.metricsLoading = true;
            try {
                const { data } = await axios.get("/api/nexus/partners/metrics", {
                    params: {
                        partner_id: this.partnerParam(),
                    },
                });
                this.metrics = data;
            } catch (error) {
                this.toast.error("Erro ao carregar indicadores.");
            } finally {
                this.metricsLoading = false;
            }
        },
        handlePartnerChange() {
            this.onboardPagination.current_page = 1;
            this.ordersPagination.current_page = 1;
            this.tokenVisible = false;
            this.revealedToken = null;
            this.fetchMetrics();
            if (this.activeTab === "onboard") {
                this.fetchOnboards();
            }
            if (this.activeTab === "sales") {
                this.handleOrdersFetch();
            }
            if (this.activeTab === "settings" && this.selectedPartnerId !== "all") {
                this.fetchSettings();
            }
        },
        statusDotClass(status) {
            if (
                ["approved", "onboarding-approved"].includes(status)
            ) {
                return "bg-green-500";
            }
            if (
                ["reproved", "onboarding-reproved"].includes(status)
            ) {
                return "bg-red-500";
            }
            return "bg-yellow-500";
        },
        async fetchOnboards() {
            this.onboardLoading = true;
            try {
                const params = {
                    partner_id: this.partnerParam(),
                    page: this.onboardPagination.current_page,
                    ...this.onboardFilters,
                };

                const { data } = await axios.get("/api/nexus/partners/onboardings", {
                    params,
                });

                this.onboardUsers = data.data;
                this.onboardPagination = {
                    ...this.onboardPagination,
                    ...data,
                };
            } catch (error) {
                this.toast.error("Erro ao carregar onboardings.");
            } finally {
                this.onboardLoading = false;
            }
        },
        handleOnboardFetch() {
            this.onboardPagination.current_page = 1;
            this.fetchOnboards();
        },
        handleOrdersFetch() {
            this.orders = [];
            this.ordersPagination.current_page = 1;
            this.fetchOrders();
        },
        async fetchOrders() {
            this.ordersLoading = true;
            try {
                const params = {
                    ...this.ordersFilters,
                    partner_id: this.partnerParam(),
                    page: this.ordersPagination.current_page,
                };

                const { data } = await axios.get("/api/nexus/partners/orders", {
                    params,
                });

                this.orders = data.data;
                this.ordersPagination = {
                    ...this.ordersPagination,
                    ...data.meta,
                };
            } catch (error) {
                this.toast.error("Erro ao carregar vendas.");
            } finally {
                this.ordersLoading = false;
            }
        },
        manageUser(userId) {
            window.location.href = `/user/${userId}/commissions`;
        },
        async fetchSettings() {
            if (this.selectedPartnerId === "all") {
                return;
            }
            this.settingsLoading = true;
            this.tokenVisible = false;
            this.revealedToken = null;
            try {
                const { data } = await axios.get(
                    `/api/nexus/partners/${this.selectedPartnerId}`,
                );
                this.clientShowData = data;
                this.availableEvents = data.available_events || [];

                const settingsData = {
                    client: data.client,
                    settings: data.settings,
                    available_events: data.available_events,
                };
                this.applySettingsResponse(settingsData);
            } catch (error) {
                this.toast.error("Erro ao carregar configurações.");
            } finally {
                this.settingsLoading = false;
            }
        },
        async updateSettings() {
            if (this.selectedPartnerId === "all") {
                return;
            }
            this.settingsSaving = true;
            try {
                const { data } = await axios.put(
                    `/api/nexus/partners/${this.selectedPartnerId}/settings`,
                    {
                        client_active: this.settingsForm.client_active,
                        user_limit: this.settingsForm.user_limit,
                    },
                );
                this.applySettingsResponse(data);
                this.toast.success("Configurações salvas com sucesso.");
            } catch (error) {
                this.toast.error("Erro ao salvar configurações.");
            } finally {
                this.settingsSaving = false;
            }
        },
        applySettingsResponse(data) {
            this.settings = data;
            this.settingsForm = {
                client_active: data.client.active,
                user_limit: data.settings.user_limit,
            };
            this.limitEnabled = data.settings.user_limit !== null && data.settings.user_limit !== undefined;
            this.limitValue = data.settings.user_limit;
        },
        handleLimitToggle(enabled) {
            if (!enabled) {
                if (!window.confirm("Deseja remover o limite de onboarding via Nexus?")) {
                    this.limitEnabled = true;
                    return;
                }
                this.limitValue = null;
                this.settingsForm.user_limit = null;
                this.updateSettings();
                return;
            }
            this.limitValue = this.limitValue || 1;
        },
        saveLimit() {
            if (!this.limitEnabled) {
                return;
            }
            if (!this.limitValue || this.limitValue < 1) {
                this.toast.error("Informe um limite válido.");
                return;
            }
            if (!window.confirm("Confirma a atualização do limite de onboarding?")) {
                return;
            }
            this.settingsForm.user_limit = this.limitValue;
            this.updateSettings();
        },
        isEventActive(event) {
            return this.settings?.settings?.events?.includes(event);
        },
        formatEventLabel(event) {
            return event
                .split("_")
                .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                .join(" ");
        },

        // ---- CRUD Methods ----
        resetClientForm() {
            this.clientForm = {
                name: "",
                active: true,
                webhook_endpoint: "",
                files_domain: "",
                events: [],
                sales_active: true,
                onboarding_active: true,
                user_limit: null,
            };
            this.createdToken = null;
            this.editingClientId = null;
        },
        openCreateModal() {
            this.resetClientForm();
            this.clientForm.events = [...this.availableEvents];
            this.showClientModal = true;
        },
        openEditModal() {
            if (!this.clientShowData) return;
            this.editingClientId = this.selectedPartnerId;
            this.createdToken = null;
            this.clientForm = {
                name: this.clientShowData.client.name,
                active: this.clientShowData.client.active,
                webhook_endpoint: this.clientShowData.settings.webhook_endpoint || "",
                files_domain: this.clientShowData.settings.files_domain || "",
                events: [...(this.clientShowData.settings.events || [])],
                sales_active: this.clientShowData.settings.sales_active,
                onboarding_active: this.clientShowData.settings.onboarding_active,
                user_limit: this.clientShowData.settings.user_limit,
            };
            this.showClientModal = true;
        },
        async saveClient() {
            this.clientSaving = true;
            try {
                if (this.editingClientId) {
                    await axios.put(
                        `/api/nexus/partners/${this.editingClientId}`,
                        this.clientForm,
                    );
                    this.toast.success("Parceiro atualizado com sucesso.");
                    this.showClientModal = false;
                    await this.fetchPartners();
                    this.fetchSettings();
                } else {
                    const { data } = await axios.post("/api/nexus/partners", this.clientForm);
                    this.createdToken = data.client.token;
                    this.toast.success("Parceiro criado com sucesso.");
                    await this.fetchPartners();
                }
            } catch (error) {
                const msg = error.response?.data?.message || "Erro ao salvar parceiro.";
                this.toast.error(msg);
            } finally {
                this.clientSaving = false;
            }
        },
        async deleteClient() {
            if (!window.confirm("Tem certeza que deseja excluir este parceiro? Esta ação não pode ser desfeita.")) {
                return;
            }
            try {
                await axios.delete(`/api/nexus/partners/${this.selectedPartnerId}`);
                this.toast.success("Parceiro removido com sucesso.");
                this.selectedPartnerId = "all";
                this.clientShowData = null;
                this.settings = null;
                await this.fetchPartners();
                this.fetchMetrics();
                this.changeTab("onboard");
            } catch (error) {
                const msg = error.response?.data?.message || "Erro ao excluir parceiro.";
                this.toast.error(msg);
            }
        },

        // ---- Token ----
        async toggleToken() {
            if (this.tokenVisible) {
                this.tokenVisible = false;
                return;
            }
            if (this.revealedToken) {
                this.tokenVisible = true;
                return;
            }
            this.tokenRevealing = true;
            try {
                const { data } = await axios.get(`/api/nexus/partners/${this.selectedPartnerId}/token`);
                this.revealedToken = data.token;
                this.tokenVisible = true;
            } catch (error) {
                this.toast.error("Erro ao revelar token.");
            } finally {
                this.tokenRevealing = false;
            }
        },
        async copyToClipboard(text) {
            try {
                await navigator.clipboard.writeText(text);
                this.toast.success("Copiado!");
            } catch {
                this.toast.error("Erro ao copiar.");
            }
        },

        // ---- Bank Accounts ----
        async fetchBankAccounts() {
            this.baLoading = true;
            try {
                const { data } = await axios.get("/api/nexus/bank-accounts", {
                    params: {
                        partner_id: this.partnerParam(),
                        search: this.baSearch || undefined,
                        status: this.baStatusFilter || undefined,
                        show_deleted: this.baDeletedFilter || undefined,
                        page: this.baPagination.current_page,
                    },
                });
                this.bankAccounts = data.data;
                this.baPagination = { ...this.baPagination, ...data };
            } catch {
                this.toast.error("Erro ao carregar contas bancárias.");
            } finally {
                this.baLoading = false;
            }
        },
        handleBaSearch() {
            this.baPagination.current_page = 1;
            this.fetchBankAccounts();
        },
        async fetchBanks() {
            try {
                const { data } = await axios.get("/api/bank-accounts/banks");
                this.banks = data;
            } catch {
                this.toast.error("Erro ao carregar lista de bancos.");
            }
        },
        baStatusDot(status) {
            if (status === "approved") return "bg-green-500";
            if (status === "reproved") return "bg-red-500";
            return "bg-yellow-500";
        },
        baStatusLabel(status) {
            const map = { approved: "Aprovado", pending: "Pendente", reproved: "Reprovado" };
            return map[status] || status || "-";
        },
        openBaEditModal(account) {
            this.editingBa = account;
            this.baForm = {
                holder_name: account.holder_name || "",
                bank_id: account.bank_id,
                type: account.type,
                branch_number: account.branch_number || "",
                account_number: account.account_number || "",
                account_check_digit: account.account_check_digit || "",
                pix_key: account.pix_key || "",
                receiver_type: account.receiver_type || "pix",
            };
            this.showBaModal = true;
        },
        async saveBa() {
            this.baSaving = true;
            try {
                await axios.put(`/api/nexus/bank-accounts/${this.editingBa.id}`, this.baForm);
                this.toast.success("Conta bancária atualizada.");
                this.showBaModal = false;
                this.fetchBankAccounts();
            } catch (error) {
                this.toast.error(error.response?.data?.message || "Erro ao salvar.");
            } finally {
                this.baSaving = false;
            }
        },
        async deleteBa(account) {
            if (!window.confirm(`Excluir conta bancária #${account.id}?`)) return;
            this.baActionLoading = true;
            try {
                await axios.delete(`/api/nexus/bank-accounts/${account.id}`);
                this.toast.success("Conta excluída.");
                this.fetchBankAccounts();
            } catch (error) {
                this.toast.error(error.response?.data?.message || "Erro ao excluir.");
            } finally {
                this.baActionLoading = false;
            }
        },
        async restoreBa(account) {
            if (!window.confirm(`Restaurar conta bancária #${account.id}?`)) return;
            this.baActionLoading = true;
            try {
                await axios.post(`/api/nexus/bank-accounts/${account.id}/restore`);
                this.toast.success("Conta restaurada.");
                this.fetchBankAccounts();
            } catch (error) {
                this.toast.error(error.response?.data?.message || "Erro ao restaurar.");
            } finally {
                this.baActionLoading = false;
            }
        },
        async generateBaCode(account) {
            if (!window.confirm(`Gerar código para conta #${account.id}?`)) return;
            this.baActionLoading = true;
            try {
                const { data } = await axios.post(`/api/nexus/bank-accounts/${account.id}/generate-code`);
                this.toast.success(`Código gerado: ${data.code}`);
                this.fetchBankAccounts();
            } catch (error) {
                this.toast.error(error.response?.data?.message || "Erro ao gerar código.");
            } finally {
                this.baActionLoading = false;
            }
        },
    },
};
</script>
